import { Sparkles, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import honeyDoIcon from "@/assets/honey-do-icon.jpeg";

const Portfolio = () => {
  const apps = [
    {
      id: "honey-do",
      name: "Honey Do",
      tagline: "ADHD-Focused Family Task Manager",
      description: "A gamified task management app designed specifically for ADHD minds. Transform chores into an engaging family experience with points, streaks, and AI-powered productivity features.",
      logo: honeyDoIcon,
      url: "/portfolio/honey-do",
      externalUrl: "https://honey-do-7e04e9a3.base44.app/",
      status: "Live",
      highlights: ["Gamification", "Family Collaboration", "AI-Powered", "ADHD-Friendly"]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-4 border-accent text-accent">
              <Sparkles className="w-3 h-3 mr-1" />
              Our Applications
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Portfolio
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              Explore the applications we've built to solve real-world problems. 
              Each project showcases our commitment to thoughtful design and user-centered development.
            </p>
          </div>
        </div>
      </section>

      {/* Apps Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {apps.map((app) => (
              <Card 
                key={app.id} 
                className="overflow-hidden border-2 border-border hover:border-accent/50 transition-all hover:shadow-lg group"
              >
                <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-yellow-500/10 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-background shadow-md flex items-center justify-center overflow-hidden border border-border">
                      <img 
                        src={app.logo} 
                        alt={`${app.name} logo`} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-primary">{app.name}</h2>
                        <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                          {app.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-accent font-medium">{app.tagline}</p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <p className="text-muted-foreground mb-4 line-clamp-3">{app.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {app.highlights.map((highlight, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className="bg-primary/10 text-primary border-primary/20 text-xs"
                      >
                        {highlight}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button asChild className="flex-1 bg-accent hover:bg-accent/90 text-primary font-semibold">
                      <Link to={app.url}>
                        View Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      asChild
                      className="border-border hover:border-accent"
                    >
                      <a href={app.externalUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
            Have an App Idea?
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            We'd love to help bring your vision to life. Schedule a discovery call to discuss your project.
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-accent hover:bg-accent/90 text-primary font-semibold"
          >
            <a href="https://calendar.app.google/HfFwVFvYGx42io1B9" target="_blank" rel="noopener noreferrer">
              Schedule a Discovery Call
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
